import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ArrowRightOnRectangleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { filterNav, type HasPermission, type NavNode } from './types';

export interface UserMenuProps {
  /** Flat list of admin destinations. Groups/sections are not supported here. */
  items: NavNode[];
  hasPermission: HasPermission;
  user?: { name?: string | null; role?: string | null } | null;
  onLogout: () => void;
}

/**
 * Header avatar → dropdown of admin destinations (monitoring, user management,
 * settings) plus logout. Replaces the per-app inline logout button.
 */
export default function UserMenu({ items, hasPermission, user, onLogout }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const visible = filterNav(items, hasPermission);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const name = user?.name || 'User';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2.5 rounded-lg px-1.5 py-1 transition-colors hover:bg-muted"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Account menu"
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand text-[13px] font-semibold text-white">
          {name.charAt(0).toUpperCase()}
        </div>
        <div className="hidden sm:block text-left">
          <p className="text-[13px] font-medium text-foreground leading-tight">{name}</p>
          <p className="text-[11px] text-muted-foreground capitalize leading-tight">{user?.role || 'user'}</p>
        </div>
        <ChevronDownIcon className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-60 rounded-xl border border-border bg-card shadow-lg overflow-hidden z-50"
        >
          <div className="px-3 py-2.5 border-b border-border">
            <p className="text-[13px] font-medium text-foreground truncate">{name}</p>
            <p className="text-[11px] text-muted-foreground capitalize">{user?.role || 'user'}</p>
          </div>

          {visible.length > 0 && (
            <div className="p-1.5 space-y-0.5">
              {visible.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.to || '#'}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] transition-colors ${
                      isActive
                        ? 'bg-muted font-medium text-brand'
                        : 'text-foreground hover:bg-muted'
                    }`
                  }
                  role="menuitem"
                >
                  {item.icon && <item.icon className="h-[18px] w-[18px] flex-shrink-0" />}
                  <span className="truncate">{item.name}</span>
                </NavLink>
              ))}
            </div>
          )}

          <div className="border-t border-border p-1.5">
            <button
              onClick={() => {
                setOpen(false);
                onLogout();
              }}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] text-foreground transition-colors hover:bg-muted hover:text-destructive"
              role="menuitem"
            >
              <ArrowRightOnRectangleIcon className="h-[18px] w-[18px] flex-shrink-0" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
